import {Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import type {OrderStatusLogEntryResponseBody} from '@dms/api/new-vehicle';
import i18n from '@dms/i18n';

import {buildArray, parseDate} from 'shared/web';

type StatusLogEffectLinesProps = {
  entry: OrderStatusLogEntryResponseBody;
};

/** Hold and order version changes of a factory receipt, as the backend marked them on its row. */
export function StatusLogEffectLines(props: StatusLogEffectLinesProps) {
  const formatDate = useDateTimeFormatter();
  const facts = props.entry.facts;
  const recordedAt = isNil(props.entry.factoryRecordedAt)
    ? null
    : formatDate('dateTimeShort', parseDate(props.entry.factoryRecordedAt));
  const holdExpectedReleaseDate = facts?.holdExpectedReleaseDate;
  const orderVersion = facts?.orderVersion;
  const holdLine = match(props.entry.holdChange)
    .with('set', () =>
      [
        i18n.t('entity.newVehicle.order.factoryHold'),
        facts?.holdReason,
        isNil(holdExpectedReleaseDate)
          ? null
          : formatDate('dateMedium', parseDate(holdExpectedReleaseDate)),
      ]
        .filter(isNotNilOrEmpty)
        .join(' · ')
    )
    .with('released', () =>
      isNil(recordedAt)
        ? null
        : i18n.t('entity.newVehicle.order.factoryHoldReleased', {date: recordedAt})
    )
    .with(Pattern.nullish, () => null)
    .exhaustive();
  const versionLine =
    props.entry.orderVersionChanged === true && isNotNil(orderVersion) && isNotNil(recordedAt)
      ? i18n.t('entity.newVehicle.order.factoryOrderVersionChanged', {
          n: orderVersion,
          date: recordedAt,
        })
      : null;
  const lines = buildArray<string>().whenNotNil(holdLine).whenNotNil(versionLine);

  return (
    <>
      {lines.map((line) => (
        <Text key={line} size="xSmall">
          {line}
        </Text>
      ))}
    </>
  );
}
